//changed due to package upgrade
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import AppGridContainer from '@projectK/components/AppGridContainer';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import PaymentMethodInfo from './PayementMethodInfo';
import {
  getSavedProgress,
  getAuthenticatedProgressUserId,
  getSaveProgressUserId,
  saveProgress,
} from './handleState';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import jwtAxios from '@projectK/services/auth/JWT';
import PropTypes from 'prop-types';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import CompanyTheme from '@projectK/constants/CompanyTheme';

const PurchaseOptions = ({ onBack, onNext }) => {
  const { tournamentSlug } = useParams();
  const [purchaseOptions, setPurchaseOptions] = useState();
  const [data, setData] = useState();
  const { user } = useAuthUser();
  const savedProgress = getSavedProgress(
    tournamentSlug,
    getAuthenticatedProgressUserId(user),
  );
  const companyTheme = CompanyTheme();

  const [teams, setTeams] = useState(
    savedProgress?.data?.teamConfig?.teams ?? [],
  );
  useEffect(() => {
    if (savedProgress?.data?.purchaseOptions) {
      setData(savedProgress?.data.purchaseOptions);
      setTeams(savedProgress?.data?.teamConfig?.teams);
    }
  }, []);
  const validationSchema = Yup.object().shape(
    teams?.reduce((acc, team) => {
      acc[team?.team_name] = Yup.object().shape({
        purchase_id: Yup.string().required('Purchase option is required'),
      });
      return acc;
    }, {}),
  );
  const fetchPurchaseOptions = async () => {
    const purchase_options = {};
    for (let team of teams) {
      const endpoint = user?.id
        ? `/tournament-purchase-options/purchase-option/by-division/${team?.division_id}`
        : `/public/tournament-purchase-options/purchase-option/by-division/${team?.division_id}`;

      const response =
        team?.division_id && team?.team_id
          ? await jwtAxios.get(endpoint, {
              params: {
                filters: {
                  is_expired: false,
                },
              },
            })
          : null;
      if (response?.status === 200) {
        purchase_options[team?.team_name] = response.data?.list;
      }
    }
    setPurchaseOptions({ ...purchase_options });
  };
  useEffect(() => {
    fetchPurchaseOptions();
  }, [teams]);
  const renderPurchaseOptions = (key) => {
    return purchaseOptions?.[key]?.length > 0 ? (
      purchaseOptions?.[key].map((item) => (
        <MenuItem key={item.id} value={item.id}>
          {`Pay $${item.purchase_price}`}
        </MenuItem>
      ))
    ) : (
      <MenuItem disabled>No Purchase options</MenuItem>
    );
  };
  const renderTournamentName = (key) => {
    return purchaseOptions?.[key]?.length > 0
      ? purchaseOptions?.[key].map((item) => item.tournament_name)
      : null;
  };

  useEffect(() => {
    // run only after both teams and purchaseOptions are loaded
    if (!purchaseOptions || !teams?.length) return;

    const savedPurchaseOptions = savedProgress?.data?.purchaseOptions;
    if (savedPurchaseOptions && Object.keys(savedPurchaseOptions).length > 0) {
      setData(savedPurchaseOptions);
      return;
    }

    // check available options per team
    const autoSelect = {};
    let hasMultiple = false;

    for (let team of teams) {
      const options = purchaseOptions[team.team_name] || [];

      if (options.length === 1) {
        autoSelect[team.team_name] = { purchase_id: options[0].id };
      } else if (options.length > 1) {
        hasMultiple = true;
      }
    }
    if (!hasMultiple) {
      saveProgress(
        tournamentSlug,
        3,
        { purchaseOptions: autoSelect },
        getSaveProgressUserId(user),
      );
      onNext({});
      return;
    }
    if (Object.keys(autoSelect).length > 0) {
      setData(autoSelect);
    }
  }, [purchaseOptions, teams]);

  return (
    <>
      <PaymentMethodInfo />
      {/* <TournamentDetails tournamentSlug={tournamentSlug} /> */}
      <Box sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
        <Formik
          initialValues={
            data ||
            teams?.reduce((acc, team) => {
              acc[team?.id] = { purchase_id: '' };
              return acc;
            }, {})
          }
          onSubmit={async (values) => {
            //handle api call
            saveProgress(
              tournamentSlug,
              3,
              {
                purchaseOptions: values,
              },
              getSaveProgressUserId(user),
            );
            const nextData = [];
            for (let team of teams) {
              const purchase_id = values?.[team?.team_name]?.purchase_id;
              const selectedOption = purchaseOptions?.[team?.team_name]?.find(
                (option) => option.id === purchase_id,
              );

              let obj = { ...team };

              if (selectedOption) {
                const newData = {
                  item_price: selectedOption.purchase_price,
                  ...selectedOption,
                  quantity: 1,
                  tournament_org_id: savedProgress?.data?.orgId,
                };
                obj = { ...team, ...newData };
              }

              nextData.push(obj);
            }
            saveProgress(
              tournamentSlug,
              2,
              {
                payment: nextData,
              },
              getSaveProgressUserId(user),
            );
            onNext({});
          }}
          validationSchema={validationSchema}
          enableReinitialize
        >
          {({ setFieldValue, values, isSubmitting, handleSubmit, errors }) => (
            <>
              <Form noValidate autoComplete='off'>
                <Typography variant='h4' fontWeight='bold' sx={{ mb: 2 }}>
                  Select Payment option
                </Typography>
                <AppGridContainer>
                  {teams?.map((team) => {
                    const tournamentNameArray = renderTournamentName(
                      team?.team_name,
                    );
                    const tournamentName =
                      tournamentNameArray?.length > 0
                        ? tournamentNameArray[0]
                        : tournamentNameArray;
                    let teamLabel = [
                      team?.team_name || 'Unnamed Team',
                      team?.division_name,
                      tournamentName,
                    ]
                      .filter(Boolean)
                      .join(' - ');
                    return (
                      <>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            name='team'
                            variant='outlined'
                            sx={{
                              width: '100%',
                            }}
                            label={teamLabel}
                            values={team?.team_name}
                            disabled
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            sx={{ width: '100%' }}
                            select
                            variant='outlined'
                            label='Purchase Option'
                            name={`${team?.id}.purchase_id`}
                            value={values?.[team?.team_name]?.purchase_id || ''}
                            onChange={(event) => {
                              setFieldValue(
                                `${team?.team_name}.purchase_id`,
                                event.target.value,
                              );
                            }}
                            error={!!errors?.[team?.team_name]?.purchase_id}
                            helperText={errors?.[team?.team_name]?.purchase_id}
                          >
                            {!purchaseOptions && (
                              <MenuItem disabled>No Purchase options</MenuItem>
                            )}

                            {renderPurchaseOptions(team?.team_name)}
                          </TextField>
                        </Grid>
                      </>
                    );
                  })}
                </AppGridContainer>
              </Form>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row', // always side-by-side
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1.5, // space between buttons
                  mt: 4,
                  pt: 2,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  flexWrap: 'wrap', // allow wrapping if screen too small
                }}
              >
                <Button
                  onClick={onBack}
                  variant='contained'
                  sx={{
                    minWidth: { xs: 'auto', sm: 100 },
                    fontSize: { xs: '0.875rem', sm: '1rem' }, // smaller font on mobile
                    py: { xs: 1, sm: 1.5 }, // reduced vertical padding on mobile
                    backgroundColor: companyTheme
                      ? companyTheme.primary.main
                      : '#2196f3',
                    '& .MuiButton-startIcon': {
                      marginRight: '4px',
                    },
                    '&:hover': {
                      backgroundColor: companyTheme
                        ? companyTheme.primary.dark
                        : '#1976d2',
                    },
                  }}
                >
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  variant='contained'
                  disabled={isSubmitting}
                  sx={{
                    minWidth: { xs: 'auto', sm: 100 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    py: { xs: 1, sm: 1.5 },
                    backgroundColor: companyTheme
                      ? companyTheme.primary.main
                      : '#2196f3',
                    '& .MuiButton-startIcon': {
                      marginRight: '4px',
                    },
                    '&:hover': {
                      backgroundColor: companyTheme
                        ? companyTheme.primary.dark
                        : '#1976d2',
                    },
                  }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} color='inherit' />
                  ) : (
                    'Go to Payment'
                  )}
                </Button>
              </Box>
            </>
          )}
        </Formik>
      </Box>
    </>
  );
};
export default PurchaseOptions;

PurchaseOptions.propTypes = {
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};
